/**
 * Client UI language helpers for Eternity.
 *
 * Server `/language` still drives Chat.tr messages; this module translates
 * visible client chrome from the same preference. Default language is Spanish.
 *
 * @license AGPLv3
 */

import { PS } from "./client-main";
import { toID } from "./battle-dex";

export type ClientLanguageID =
	| 'english' | 'spanish' | 'german' | 'french' | 'italian' | 'dutch'
	| 'portuguese' | 'turkish' | 'hindi' | 'japanese'
	| 'simplifiedchinese' | 'traditionalchinese';

/** Default for Eternity when the user has never chosen a language. */
export const DEFAULT_CLIENT_LANGUAGE: ClientLanguageID = 'spanish';

/**
 * English → Spanish UI chrome. Keys must match exact English source strings.
 * Proper names / community jargon kept when usual in ES PS rooms.
 */
const SPANISH: { [english: string]: string } = {
	// ——— Main menu ———
	"Format:": "Formato:",
	"Team:": "Equipo:",
	"Battle!": "¡Batalla!",
	"Find a random opponent": "Buscar un oponente aleatorio",
	"Teambuilder": "Teambuilder",
	"Ladder": "Ladder",
	"Tournaments": "Torneos",
	"Watch a battle": "Ver una batalla",
	"Find a user": "Buscar un usuario",
	"Friends": "Amigos",
	"Info & Resources": "Info y recursos",
	"Chat rooms": "Salas de chat",
	"Chat Rooms": "Salas de chat",
	"Lobby chat": "Chat del lobby",
	"Join lobby chat": "Unirse al lobby",
	"You are in:": "Estás en:",
	"Connecting...": "Conectando...",
	"Disconnected": "Desconectado",
	"Offline": "Desconectado",
	"Reconnect": "Reconectar",
	"Don't allow spectators": "No permitir espectadores",
	"Custom rules": "Reglas personalizadas",
	"Custom rules guide": "Guía de reglas personalizadas",
	"Rules separated by commas or lines": "Reglas separadas por comas o líneas",
	"Tera Type Preview": "Vista previa del tipo Tera",
	"Item Clause": "Cláusula de objeto",
	"Battle options": "Opciones de batalla",
	"Searching...": "Buscando...",
	"Cancel": "Cancelar",
	"Random team": "Equipo aleatorio",
	"News": "Noticias",
	"Best-of-": "Al mejor de ",

	// ——— Top bar / chrome ———
	"Close": "Cerrar",
	"Join chat": "Unirse al chat",
	"Choose name": "Elegir nombre",
	"Sound": "Sonido",
	"Options": "Opciones",
	"All tabs": "Todas las pestañas",
	"Menu": "Menú",
	"Maximize": "Maximizar",
	"Expand/Collapse": "Expandir/Contraer",
	"Loading...": "Cargando...",
	"OK": "OK",
	"Done": "Hecho",
	"Back": "Atrás",
	"Search": "Buscar",
	"Refresh": "Actualizar",
	"Copy": "Copiar",
	"Copied!": "¡Copiado!",
	"Edit": "Editar",
	"Add": "Añadir",
	"Remove": "Quitar",
	"Delete": "Eliminar",
	"Rename": "Renombrar",
	"Create": "Crear",
	"Save changes": "Guardar cambios",
	"Upload": "Subir",
	"Upload:": "Subir:",
	"Change": "Cambiar",
	"Revert": "Revertir",
	"Deselect": "Deseleccionar",
	"Validate": "Validar",
	"Share URL:": "URL para compartir:",
	"Visit": "Visitar",
	"Commands": "Comandos",
	"Chat log": "Registro del chat",

	// ——— Options ———
	"Graphics": "Gráficos",
	"Theme:": "Tema:",
	"Light": "Claro",
	"Dark theme": "Oscuro",
	"Match system theme": "Seguir el tema del sistema",
	"Layout:": "Diseño:",
	"Two panels (if wide enough)": "Dos paneles (si hay espacio)",
	"Automatic (Vertical tabs)": "Automático (pestañas verticales)",
	"Automatic (Single panel)": "Automático (un panel)",
	"Single panel": "Un panel",
	"Vertical tabs": "Pestañas verticales",
	"Automatic": "Automático",
	"Background:": "Fondo:",
	"Change Background": "Cambiar fondo",
	"Change background": "Cambiar fondo",
	"Disable animations": "Desactivar animaciones",
	"Use 2D sprites instead of 3D models": "Usar sprites 2D en lugar de modelos 3D",
	"Use modern sprites for past generations": "Usar sprites modernos en generaciones pasadas",
	"Chat": "Chat",
	"Block DMs": "Bloquear MPs",
	"Block PMs": "Bloquear MPs",
	"Block challenges": "Bloquear desafíos",
	"Block Challenges": "Bloquear desafíos",
	"Show DMs in chatrooms": "Mostrar MPs en salas de chat",
	"Show PMs in chat rooms": "Mostrar MPs en salas de chat",
	"Do not highlight when your name is said in chat": "No resaltar cuando digan tu nombre en el chat",
	"Highlight when your name is said in chat": "Resaltar cuando digan tu nombre en el chat",
	"Confirm before leaving a room": "Confirmar antes de salir de una sala",
	"Confirm before refreshing": "Confirmar antes de recargar",
	"Language:": "Idioma:",
	"Tournaments:": "Torneos:",
	"Always notify": "Notificar siempre",
	"Notify when joined": "Notificar al unirse",
	"Notifications": "Notificaciones",
	"No Notifications": "Sin notificaciones",
	"Hide": "Ocultar",
	"Timestamps:": "Marcas de tiempo:",
	"Timestamps in chat rooms:": "Marcas de tiempo en salas:",
	"Timestamps in DMs:": "Marcas de tiempo en MPs:",
	"Timestamps in PMs:": "Marcas de tiempo en MPs:",
	"Off": "Desactivado",
	"Chat preferences:": "Preferencias de chat:",
	"Text formatting": "Formato de texto",
	"Text formatting...": "Formato de texto...",
	"Usable formatting:": "Formato disponible:",
	"Change name": "Cambiar nombre",
	"Log out": "Cerrar sesión",
	"Avatar...": "Avatar...",
	"Password...": "Contraseña...",
	"Register": "Registrarse",
	"Download teams from server:": "Descargar equipos del servidor:",
	"Add status": "Añadir estado",
	"Volume": "Volumen",
	"Effects:": "Efectos:",
	"Music:": "Música:",
	"Notifications:": "Notificaciones:",
	"Mute all": "Silenciar todo",
	"(muted)": "(silenciado)",

	// ——— Footer ———
	"Pokédex": "Pokédex",
	"Replays": "Replays",
	"Forum": "Foro",
	"Rules": "Reglas",
	"Credits": "Créditos",
	"Privacy": "Privacidad",

	// ——— Rooms ———
	"Find an online user": "Buscar un usuario conectado",
	"Watch an active battle": "Ver una batalla activa",
	"users online": "usuarios conectados",
	"user online": "usuario conectado",
	"active battles": "batallas activas",
	"active battle": "batalla activa",
	"user": "usuario",
	"users": "usuarios",
	"(All rooms)": "(Todas las salas)",
	"Join or search for rooms": "Unirse o buscar salas",
	"Official chat rooms": "Salas oficiales",
	"Subrooms:": "Subsalas:",
	"Close Room": "Cerrar sala",

	// ——— Ladder ———
	"username prefix": "prefijo de usuario",
	"Username prefix": "Prefijo de usuario",
	"Name": "Nombre",
	"Format List": "Lista de formatos",
	"Seasonal rankings": "Rankings de temporada",
	"No one has played any ranked games yet.": "Nadie ha jugado partidas ranked todavía.",

	// ——— User / login ———
	"Username:": "Usuario:",
	"Look up": "Buscar",
	"Add friend": "Añadir amigo",
	"Ignore": "Ignorar",
	"Unignore": "Dejar de ignorar",
	"Report": "Reportar",
	"Unmute": "Desmutear",
	"Unlock": "Unlock",
	"Unnamelock": "Unnamelock",
	"Mute": "Mute",
	"Hourmute": "Hourmute",
	"Ban": "Ban",
	"Weekban": "Weekban",
	"Lock": "Lock",
	"Weeklock": "Weeklock",
	"Namelock": "Namelock",
	"Modlog": "Modlog",
	"Global moderation": "Moderación global",
	"Global modlog": "Modlog global",
	"Reason (optional):": "Motivo (opcional):",
	"Log in": "Iniciar sesión",
	"Password:": "Contraseña:",
	"Show password": "Mostrar contraseña",
	"Logging in...": "Iniciando sesión...",
	"Try another name": "Probar otro nombre",
	"Choose an avatar or": "Elige un avatar o",
	"Forfeit and close": "Rendirse y cerrar",
	"Just forfeit": "Solo rendirse",
	"Replace player": "Reemplazar jugador",
	"Replacement player's name:": "Nombre del reemplazo:",
	"Replace": "Reemplazar",
	"Change your password:": "Cambia tu contraseña:",
	"Old password:": "Contraseña actual:",
	"New password:": "Contraseña nueva:",
	"New password (confirm):": "Contraseña nueva (confirmar):",
	"Change password": "Cambiar contraseña",
	"Register your account:": "Registra tu cuenta:",
	"Password (confirm):": "Contraseña (confirmar):",
	"What is this pokemon?": "¿Qué Pokémon es este?",
	"Battles:": "Batallas:",
	"Chatrooms:": "Salas:",
	"Private rooms:": "Salas privadas:",
	"OFFLINE": "DESCONECTADO",
	"Challenge": "Desafiar",
	"Chat Self": "Chatear consigo",
	"Challenging...": "Desafiando...",
	"Accept": "Aceptar",
	"Reject": "Rechazar",
	"Accepting...": "Aceptando...",
	"Choose a name before sending messages": "Elige un nombre antes de enviar mensajes",

	// ——— Background picker ———
	"Set as background": "Usar de fondo",
	"Default": "Por defecto",
	"Random": "Aleatorio",
	"Server": "Servidor",
	"Official": "Oficial",
	"Custom": "Personalizado",

	// ——— Battle list / spectator ———
	"(All formats)": "(Todos los formatos)",
	"Minimum Elo:": "Elo mínimo:",
	"None": "Ninguno",
	"No battles are going on": "No hay batallas en curso",
	"Battle Controls": "Controles de batalla",
	"Play": "Reproducir",
	"Pause": "Pausa",
	"First turn": "Primer turno",
	"Prev turn": "Turno anterior",
	"Skip turn": "Saltar turno",
	"Skip to end": "Ir al final",
	"Switch viewpoint": "Cambiar punto de vista",
	"Go to turn": "Ir al turno",
	"Skip": "Saltar",
	"Skip animation": "Saltar animación",
	"Battle": "Atacar",
	"Switch": "Cambiar",
	"Shift": "Desplazar",
	"Team": "Equipo",
	"Move to center": "Mover al centro",
	"Waiting for opponent...": "Esperando al oponente...",
	"Team so far": "Equipo hasta ahora",
	"How will you start the battle?": "¿Cómo empezarás la batalla?",
	"What about the rest of your team?": "¿Y el resto de tu equipo?",
	"Download replay": "Descargar replay",
	"Upload and share replay": "Subir y compartir replay",
	"Copiar link replay": "Copiar link replay",
	"Replay": "Replay",
	"Main menu": "Menú principal",
	"(closes this battle)": "(cierra esta batalla)",
	"Rematch": "Revancha",
	"Mega Evolution": "Megaevolución",
	"Ultra Burst": "Ultraexplosión",
	"Z-Power": "Poder Z",
	"Dynamax": "Dynamax",
	"Gigantamax": "Gigamax",
	"Try Fight button": "Probar botón Luchar",
	"(empty slot)": "(hueco vacío)",
	"Error": "Error",
	"In this battle": "En esta batalla",
	"Forfeit": "Rendirse",
	"Offer tie": "Ofrecer empate",
	"Hardcore mode (hide info not shown in-game)": "Modo hardcore (ocultar info que no se ve en el juego)",
	"Ignore spectators": "Ignorar espectadores",
	"Ignore opponent": "Ignorar oponente",
	"Ignore nicknames": "Ignorar motes",
	"All battles": "Todas las batallas",
	"Invite only (hide from Battles list)": "Solo invitación (ocultar de la lista)",
	"Ignore Pokémon nicknames": "Ignorar motes de Pokémon",
	"Automatically start timer": "Iniciar el temporizador automáticamente",
	"Hardcore mode": "Modo hardcore",
	"Start at turn 0 when spectating battles": "Empezar en el turno 0 al espectar",
	"Open new battles in the right-side panel": "Abrir batallas nuevas en el panel derecho",
	"Try vertical tabs": "Probar pestañas verticales",
	"Stop Timer": "Parar temporizador",
	"Start Timer": "Iniciar temporizador",
	"Side-by-side, controls below": "Lado a lado, controles abajo",
	"Side-by-side, overlay controls": "Lado a lado, controles superpuestos",
	"Top-and-bottom, controls below": "Arriba y abajo, controles abajo",
	"Top-and-bottom, overlay controls": "Arriba y abajo, controles superpuestos",
	"Scrolling, controls below": "Desplazamiento, controles abajo",
	"Scrolling, overlay controls": "Desplazamiento, controles superpuestos",
	"Your move!": "¡Tu movimiento!",
	"Your switch!": "¡Tu cambio!",
	"Team preview!": "¡Vista previa del equipo!",
	"Move in your battle": "Elige un movimiento en tu batalla",
	"Switch in your battle": "Cambia en tu batalla",
	"Choose your team order in your battle": "Elige el orden de tu equipo",
	"Mega": "Mega",
	" Evolve and ": " Evolucionar y ",
	" Evolve (X) and ": " Evolucionar (X) y ",
	" Evolve (Y) and ": " Evolucionar (Y) y ",
	"Ultra": "Ultra",
	" Burst and ": " Explosionar y ",

	// ——— Tournament ———
	"Join": "Unirse",
	"Leave": "Salir",
	"Ready!": "¡Listo!",
	"In Progress": "En curso",
	"Signups": "Inscripciones",
	"Waiting for battles to become available...": "Esperando a que haya batallas disponibles...",
	"In-progress": "En curso",

	// ——— Teambuilder ———
	"Folders": "Carpetas",
	"All teams": "Todos los equipos",
	"All Teams": "Todos los equipos",
	"Search teams": "Buscar equipos",
	"New team": "Equipo nuevo",
	"New box": "Caja nueva",
	"Backup": "Copia de seguridad",
	"Copy/move": "Copiar/mover",
	"Uploaded": "Subido",
	"Public": "Público",
	"Paste copy here": "Pega la copia aquí",
	"Move here": "Mover aquí",
	"+ Clipboard": "+ Portapapeles",
	"Undo delete": "Deshacer borrado",
	"Convert to prefix": "Convertir a prefijo",
	"(add folder)": "(añadir carpeta)",
	"(add format folder)": "(añadir carpeta de formato)",
	"(all)": "(todos)",
	"(uncategorized)": "(sin categoría)",
	"Teams not in any folders": "Equipos sin carpeta",
	"Readable": "Legible",
	"List": "Lista",
	"Teams": "Equipos",
	"Upload changes": "Subir cambios",
	"Local": "Local",
	"Account": "Cuenta",
	"Account (public)": "Cuenta (público)",
	"Disconnected (wrong account?)": "Desconectado (¿cuenta incorrecta?)",
	"Team name:": "Nombre del equipo:",
	"Uploading...": "Subiendo...",
	"Revert to uploaded version": "Volver a la versión subida",
	"Compare": "Comparar",
	"Uploaded by:": "Subido por:",
	"Views:": "Vistas:",
	"Untitled team": "Equipo sin título",
	"Select a team": "Elige un equipo",
	"Other gens": "Otras generaciones",
	"No teams found": "No se encontraron equipos",
	"teams": "equipos",
	"Search formats": "Buscar formatos",
	"Gen 9": "Gen 9",
	"This format selector is no longer available.": "Este selector de formato ya no está disponible.",
	"This team selector is no longer available (the challenge was cancelled or something).":
		"Este selector de equipo ya no está disponible (el desafío se canceló o similar).",
	"(DESKTOP)": "(ESCRITORIO)",
	"(MOBILE HORIZONTAL)": "(MÓVIL HORIZONTAL)",
	"(MOBILE VERTICAL)": "(MÓVIL VERTICAL)",
	"(Note: The First Amendment does not apply to PS, since PS is not a government organization.)": "(Nota: la Primera Enmienda no aplica a PS, porque PS no es una organización gubernamental.)",
	"(Note: You can opt out of chat rules in private chat rooms and battle rooms, but only if all ROs or players agree to it.)": "(Nota: puedes acordar otras reglas en salas privadas y batallas, solo si todos los RO o jugadores están de acuerdo.)",
	"(Private room?)": "(¿Sala privada?)",
	"(We'll be back up in a few hours.)": "(Volveremos en unas horas.)",
	"(can't save partial exports)": "(no se pueden guardar exportaciones parciales)",
	"(doesn't delete teams)": "(no borra equipos)",
	"(public)": "(público)",
	"(turn 100+)": "(turno 100+)",
	"Add a Pokemon to your team before uploading it.": "Añade un Pokémon a tu equipo antes de subirlo.",
	"All fields are required": "Todos los campos son obligatorios",
	"An Electric-type mouse that is the mascot of the Pokémon franchise.": "Un ratón de tipo Eléctrico, mascota de la franquicia Pokémon.",
	"Auto-show spoilers:": "Mostrar spoilers automáticamente:",
	"Be nice to people. Respect people. Don't be rude or mean to people.": "Sé amable. Respeta a los demás. No seas grosero ni cruel.",
	"Bear with us as we freak out.": "Ten paciencia mientras resolvemos el caos.",
	"Cannot replace player, battle has already ended.": "No se puede reemplazar: la batalla ya terminó.",
	"Charizards": "Mew shiny",
	"Mew shiny": "Mew shiny",
	"Choose": "Elegir",
	"Cmd": "Cmd",
	"Command": "Comando",
	"Console": "Consola",
	"Copy of": "Copia de",
	"Ctrl": "Ctrl",
	"Custom rules:": "Reglas personalizadas:",
	"Discipline Appeals": "Apelaciones disciplinarias",
	"Do not spam, flame, or troll. This includes advertising, raiding, asking questions with one-word answers in the lobby, and flooding the chat such as by copy/pasting logs in the lobby.": "No hagas spam, flameos ni trolling. Incluye publicidad, raids, preguntas de una palabra en el lobby y flood (pegar logs, etc.).",
	"Don't call unnecessary attention to yourself. Don't be obnoxious. ALL CAPS and": "No llames la atención innecesariamente. No seas pesado. MAYÚSCULAS y",
	"Don't warn for untrusted links": "No avisar por links no confiables",
	"English only, unless specified otherwise.": "Solo inglés, salvo que se indique lo contrario.",
	"Enter = run command": "Enter = ejecutar comando",
	"Enter player's name": "Nombre del jugador",
	"Failed to load background image.": "No se pudo cargar la imagen de fondo.",
	"Find": "Buscar",
	"Folder name?": "¿Nombre de la carpeta?",
	"Follow US laws (PS is based in the US). No porn (minors use PS), don't distribute pirated material, and don't slander others.": "Cumple las leyes de EE. UU. (PS está allí). Sin pornografía (hay menores), no distribuyas piratería ni difames.",
	"Forfeiting makes you lose the battle. Are you sure?": "Rendirse implica perder la batalla. ¿Seguro?",
	"Gen": "Gen",
	"Global": "Global",
	"Administrator": "Administrador",
	"Moderator": "Moderador",
	"Driver": "Operador",
	"Voice": "Voz",
	"Room Owner": "Dueño de sala",
	"Host": "Anfitrión",
	"Prize Winner": "Ganador de premio",
	"Player": "Jugador",
	"Locked": "Bloqueado",
	"Muted": "Silenciado",
	"Namelocked": "Nombre bloqueado",
	"Whitelist": "Lista blanca",
	"Hardcore mode OFF: Information not available in-game is now shown.": "Modo hardcore OFF: ahora se muestra info que no aparece en el juego.",
	"Hardcore mode ON: Information not available in-game is now hidden.": "Modo hardcore ON: se oculta info que no aparece en el juego.",
	"Hidden rooms": "Salas ocultas",
	"Horizon": "Starters",
	"Starters": "Starters",
	"How the ladder works": "Cómo funciona el ladder",
	"Image is too large and can't be saved. It should be under 3.5MB or so.": "La imagen es demasiado grande. Debe pesar menos de ~3,5 MB.",
	"Look up a specific user's rating": "Consultar el rating de un usuario",
	"Make [[clickable links]] unclickable": "Hacer que los [[links clicables]] no lo sean",
	"Mega Evolution X": "Megaevolución X",
	"Mega Evolution Y": "Megaevolución Y",
	"Moderate in": "Moderar en",
	"Moderators have discretion to punish any behaviour they deem inappropriate, whether or not it's on this list. If you disagree with a moderator ruling, appeal to an administrator (a user with ~ next to their name) or": "Los moderadores pueden sancionar conductas inapropiadas aunque no estén en esta lista. Si no estás de acuerdo, apela a un administrador (usuario con ~) o",
	"Must use on an uploaded team.": "Solo se puede usar en un equipo subido.",
	"Names can't contain slashes, since they're used as a folder separator.": "Los nombres no pueden llevar barras / (separan carpetas).",
	"Names can't contain the character |, since they're used for storing teams.": "Los nombres no pueden llevar el carácter | (se usa al guardar equipos).",
	"New": "Nuevo",
	"New name required": "Se requiere un nombre nuevo",
	"No cheating. Don't exploit bugs to gain an unfair advantage. Don't game the system (by intentionally losing against yourself or a friend in a ladder match, by timerstalling, etc). Don't impersonate staff if you're not.": "No hagas trampas. No abuses de bugs. No manipules el sistema (perder a propósito, timerstall, etc.). No te hagas pasar por staff.",
	"No formats": "Sin formatos",
	"No minimodding: don't mod if it's not your job. Don't tell people they'll be muted, don't ask for people to be muted, and don't talk about whether or not people should be muted ('inb4 mute', etc). This applies to bans and other punishments, too.": "No minimodees: no moderes si no es tu trabajo. No amenaces con mutes ni pidas mutes ('inb4 mute', etc.). Lo mismo con bans y otras sanciones.",
	"No sex. Don't discuss anything sexually explicit, not even in private messages, not even if you're both adults.": "Sin contenido sexual. Ni siquiera en MPs, aunque seáis adultos.",
	"Not found": "No encontrado",
	"Not found in the Teams database. Maybe you uploaded it on a different account?": "No está en la base de equipos. ¿Lo subiste con otra cuenta?",
	"Not in a folder": "Sin carpeta",
	"Ocean": "Greninja vs Charizard",
	"Greninja vs Charizard": "Greninja vs Charizard",
	"Passwords do not match": "Las contraseñas no coinciden",
	"Pokémon Showdown Rules": "Reglas de Pokémon Showdown",
	"Pokémon Showdown is offline due to a DDoS attack!": "¡Pokémon Showdown está offline por un ataque DDoS!",
	"Pokémon Showdown is offline due to technical difficulties!": "¡Pokémon Showdown está offline por problemas técnicos!",
	"Pokémon Showdown! (beta)": "Pokémon Showdown! (beta)",
	"Pop-out": "Ventana aparte",
	"Possible secret room": "Posible sala secreta",
	"Room not found": "Sala no encontrada",
	"Search results": "Resultados de búsqueda",
	"Searching in": "Buscando en",
	"Shaymin": "Fantasmas",
	"Fantasmas": "Fantasmas",
	"La Última Cena": "La Última Cena",
	"Solid blue": "Mega Mewtwo",
	"Mega Mewtwo": "Mega Mewtwo",
	"Spectators ignored.": "Espectadores ignorados.",
	"Spectators no longer ignored.": "Espectadores ya no ignorados.",
	"Start a team-locked best-of-n series": "Iniciar serie al mejor de n con equipo fijo",
	"Stored in cookies on your computer. Warning: Your browser might delete these. Make sure to use backups.": "Se guardan en cookies. Aviso: el navegador puede borrarlas. Haz copias de seguridad.",
	"Subroom of": "Subsala de",
	"Suppress": "Suprimir",
	"Team deleted": "Equipo borrado",
	"Team doesn't exist": "El equipo no existe",
	"Team not found:": "Equipo no encontrado:",
	"Team was deleted": "El equipo fue borrado",
	"Teambuilding resources for": "Recursos de teambuilding para",
	"Textarea not found": "Textarea no encontrada",
	"This is a disconnected team. This could be because you uploaded it on a different account, or because you deleted or un-uploaded it on a different computer. For safety, you can't edit this team. You can, however, delete it, or make a copy (which will be editable).": "Equipo desconectado (otra cuenta u otro PC). Por seguridad no se puede editar; sí borrar o hacer una copia editable.",
	"This is someone else's account. Sorry.": "Esta cuenta es de otra persona. Lo siento.",
	"This team is for a different account. Please log into the correct account to update it.": "Este equipo es de otra cuenta. Entra con la cuenta correcta para actualizarlo.",
	"Timer": "Temporizador",
	"Unrecognized command:": "Comando no reconocido:",
	"Upload for": "Subir para",
	"Uploaded to the Teams database publicly. Share with the URL or people can find it by searching.": "Subido públicamente a la base de equipos. Compártelo con la URL o se puede encontrar buscando.",
	"Uploaded to the Teams database. You can share with the URL.": "Subido a la base de equipos. Puedes compartirlo con la URL.",
	"Usernames": "Nombres de usuario",
	"Usernames may not be derogatory or insulting in nature, to an individual or group (insulting yourself is okay as long as it's not too serious).": "Los nicks no pueden ser insultantes hacia personas o grupos (insultarte a ti mismo está bien si no es grave).",
	"Usernames may not directly reference sexual activity, or be excessively disgusting.": "Los nicks no pueden referirse a actividad sexual ni ser excesivamente asquerosos.",
	"Usernames may not impersonate a recognized user (a user with %, @, #, or ~ next to their name) or a famous person/organization that uses PS or is associated with Pokémon.": "Los nicks no pueden suplantar a staff (%, @, #, ~) ni a personas/organizaciones famosas de PS o Pokémon.",
	"Wait for this countdown to finish first...": "Espera a que termine la cuenta atrás...",
	"We reserve the right to tell you to stop discussing moderator decisions if you become unreasonable or belligerent": "Nos reservamos el derecho a cortar discusiones sobre decisiones de moderación si te pones hostil o irracional",
	"Wrong export mode": "Modo de exportación incorrecto",
	"You can still invite spectators by giving them the URL or using the /invite command": "Aún puedes invitar espectadores con la URL o /invite",
	"You have been successfully registered.": "Te has registrado correctamente.",
	"You have been warned for breaking the rules.": "Has recibido un aviso por romper las reglas.",
	"You must select a format first.": "Primero debes elegir un formato.",
	"You need to go into the Teambuilder and build a team for this format.": "Entra al Teambuilder y crea un equipo para este formato.",
	"You're already searching for a": "Ya estás buscando una partida de",
	"Your file": "Tu archivo",
	"Your password was successfully changed!": "¡Contraseña cambiada correctamente!",
	"Your username can be chosen and changed at any time. Keep in mind:": "Puedes elegir y cambiar tu nombre cuando quieras. Ten en cuenta:",
	"action formatting": "formato de acción",
	"are acceptable to emphasize things, but should be used sparingly, not all the time.": "valen para enfatizar, pero con moderación, no todo el rato.",
	"background by": "fondo por",
	"battle...": "batalla...",
	"bold": "negrita",
	"box": "caja",
	"code formatting": "formato de código",
	"color": "color",
	"empty": "vacío",
	"folder": "carpeta",
	"for": "para",
	"formatting": "formato",
	"found": "encontrados",
	"helpful resources for": "recursos útiles para",
	"if not:": "si no:",
	"if you registered this name:": "si registraste este nombre:",
	"in": "en",
	"is not a valid team.": "no es un equipo válido.",
	"italics": "cursiva",
	"loading Google log-in button": "cargando botón de Google",
	"matching": "coinciden",
	"more": "más",
	"on": "el",
	"search results": "resultados de búsqueda",
	"sec": "s",
	"shareable URL": "URL compartible",
	"shareable/searchable URL": "URL compartible/buscable",
	"strikethrough": "tachado",
	"subscript": "subíndice",
	"superscript": "superíndice",
	"team": "equipo",
	"team in folder": "equipo en carpeta",
	"the Smogon Dex": "la Dex de Smogon",
	"these things": "estas cosas",
	"to?": "¿a?",
	"total": "total",
	"users": "usuarios",
	"you have no teams in this folder": "no tienes equipos en esta carpeta",
	"you have no teams lol": "no tienes equipos jaja",
	"you have no teams matching": "no hay equipos que coincidan",

	// ——— Resources panel ———
	"Resources": "Recursos",
	"PS! Informational Resources": "Recursos informativos de PS!",
	"PS! is a wide and varied site, with more facets than can be covered here easily.":
		"PS! es un sitio amplio y variado, con más facetas de las que caben aquí fácilmente.",
	"While this page chiefly documents the ever-shifting set of commands available to PS! users, here are some useful resources for newcomers:":
		"Aunque esta página documenta sobre todo el conjunto cambiante de comandos disponibles para usuarios de PS!, aquí tienes recursos útiles para novatos:",
	"Beginner's Guide to Pokémon Showdown": "Guía para principiantes de Pokémon Showdown",
	"An introduction to competitive Pokémon": "Introducción al Pokémon competitivo",
	"What do 'OU', 'UU', etc mean?": "¿Qué significan 'OU', 'UU', etc.?",
	"What are the rules for each format?": "¿Cuáles son las reglas de cada formato?",
	"What is 'Sleep Clause' and other clauses?": "¿Qué es la 'Sleep Clause' y otras cláusulas?",
	"Next Steps for Competitive Battling": "Próximos pasos en batallas competitivas",
	"Report a user": "Reportar a un usuario",
	"Join the Help room for live help": "Unirse a la sala Help para ayuda en vivo",
	"Within any of the chats, and in private messages, it is possible to type in commands (messages beginning with":
		"En cualquier chat y en mensajes privados, puedes escribir comandos (mensajes que empiezan con",
	") to perform a particular action. A great number of these commands exist, with some only available to certain users. For instance, you can broadcast commands to others with the":
		") para realizar una acción concreta. Hay muchísimos comandos, algunos solo para ciertos usuarios. Por ejemplo, puedes difundir comandos a otros con el prefijo",
	"prefix, but only when you're a player in a battle or a Voice (+) user.":
		", pero solo si eres jugador en una batalla o usuario Voice (+).",
	"For more information on ranks, type": "Para más info sobre rangos, escribe",
	"in any chat.": "en cualquier chat.",
	'You can also use the "chat self" button on your username in the top right if you need a place to send these commands without joining a room.':
		'También puedes usar el botón "chat self" en tu nombre arriba a la derecha si necesitas enviar comandos sin unirte a una sala.',
	"Here's a list of the most useful commands for the average Pokémon Showdown experience:":
		"Aquí tienes los comandos más útiles para la experiencia típica de Pokémon Showdown:",
	"(replace / with ! to broadcast. Broadcasting requires: + % @ # ~)":
		"(cambia / por ! para difundir. Difundir requiere: + % @ # ~)",
	"For an overview of room commands, use": "Para un resumen de comandos de sala, usa",
	"For details of a specific command, you can use": "Para detalles de un comando concreto, usa",
	", for example": ", por ejemplo",
	"A complete list of commands available to regular users is provided below. Use":
		"Abajo hay la lista completa de comandos para usuarios normales. Usa",
	"for more in-depth information on how to use them.":
		"para información más detallada sobre cómo usarlos.",
	"Collapse all": "Contraer todo",
	"Filter by command name": "Filtrar por nombre de comando",
	"Loading command data, please try again in a few moments...":
		"Cargando datos de comandos, inténtalo de nuevo en unos momentos...",
	"Chat plugin": "Chat plugin",
	"Core commands": "Comandos principales",

	// ——— Ladder help page ———
	"Our ladder displays three ratings: Elo, GXE, and Glicko-1.":
		"Nuestro ladder muestra tres ratings: Elo, GXE y Glicko-1.",
	"is the main ladder rating. It's a pretty normal ladder rating: goes up when you win and down when you lose.":
		"es el rating principal del ladder. Es un rating normal: sube al ganar y baja al perder.",
	"(Glicko X-Act Estimate) is an estimate of your win chance against an average ladder player.":
		"(Glicko X-Act Estimate) estima tu probabilidad de victoria contra un jugador medio del ladder.",
	"Glicko-1 is": "Glicko-1 es",
	"another rating system": "otro sistema de rating",
	"It has rating and deviation values.": "Tiene valores de rating y desviación.",
	"(Converging Order Invariant Ladder) is used for suspect tests. The more games you play, the closer it will get to your GXE × 4000. How fast it reaches GXE × 4000 depends on":
		"(Converging Order Invariant Ladder) se usa en suspect tests. Cuantas más partidas juegues, más se acerca a tu GXE × 4000. La velocidad depende de",
	"a custom B value": "un valor B personalizado",
	"which is different for each suspect test.": "que es distinto en cada suspect test.",
	"Note that win/loss should not be used to estimate skill, since who you play against is much more important than how many times you win or lose. Our other stats like Elo and GXE are much better for estimating skill.":
		"No uses victorias/derrotas para estimar nivel: contra quién juegas importa más que cuántas veces ganas o pierdes. Elo y GXE son mejores indicadores.",

	// ——— Battle prompts / errors ———
	"Maxed with no max moves": "Dynamax activado sin movimientos max",
	"No Z moves": "Sin movimientos Z",

	// ——— Classic client layout ———
	"Left and right panels": "Paneles izquierdo y derecho",

	"You reconnected.": "Te has reconectado.",
	"You reconnected to ": "Te has reconectado a ",
	"You disconnected.": "Te has desconectado.",
	"Note: Scrollback doesn't go all the way back to when you disconnected.": "Nota: el historial no llega hasta el momento en que te desconectaste.",

	// ——— Teambuilder ———
	"Form": "Ficha",
	"Import/Export": "Importar/Exportar",
	"Zoom out forms": "Alejar formularios",
	"Zoom out search results": "Alejar resultados de búsqueda",
	"Compact": "Compacto",
	"Comfortable": "Cómodo",
	"Defensive coverage": "Cobertura defensiva",
	"Pokemon": "Pokémon",
	"Details": "Detalles",
	"Level": "Nivel",
	"Shiny": "Variocolor",
	"Tera": "Tera",
	"H.P.": "P.O.",
	"H. Power": "P. Oculto",
	"Gender": "Género",
	"Moves": "Movimientos",
	"Stats": "Estadísticas",
	"Ability": "Habilidad",
	"Item": "Objeto",
	"Nickname": "Mote",
	"Delete": "Borrar",
	"Import": "Importar",
	"Add to clipboard": "Añadir al portapapeles",
	"Copy/Move": "Copiar/Mover",
	"Import/Export Set": "Importar/Exportar set",
	"(no ability)": "(sin habilidad)",
	"(choose ability)": "(elige habilidad)",
	"(no item)": "(sin objeto)",
	" Paste exported teams, pokepaste URLs, or JSON here": " Pega equipos exportados, URLs de pokepaste o JSON aquí",
	"Nickname:": "Mote:",
	"Level:": "Nivel:",
	"Shiny:": "Variocolor:",
	"Yes": "Sí",
	"No": "No",
	"Gender:": "Género:",
	"Male": "Macho",
	"Female": "Hembra",
	"Unknown": "Desconocido",
	"Happiness:": "Felicidad:",
	"Dynamax Level:": "Nivel Dynamax:",
	"Hidden Power Type:": "Tipo de Poder Oculto:",
	"Tera Type:": "Tipo Tera:",
	"Tera Type": "Tipo Tera",
	"Form:": "Forma:",
	"(You probably want to change the team's levels by changing the format, not here)":
		"(Probablemente quieras cambiar los niveles del equipo cambiando el formato, no aquí)",
	"EVs, IVs, and Nature": "EVs, IVs y Naturaleza",
	"Base": "Base",
	"EVs": "EVs",
	"IVs": "IVs",
	"DVs": "DVs",
	"AVs": "AVs",
	"Points": "Puntos",
	"Remaining:": "Restantes:",
	"Nature:": "Naturaleza:",
	"Nature": "Naturaleza",
	"Attack": "Ataque",
	"Defense": "Defensa",
	"Sp. Atk.": "At. Esp.",
	"Sp. Def.": "Def. Esp.",
	"Speed": "Velocidad",
	"Special": "Especial",
	"Spc": "Esp",
	"HP": "PS",
	"Atk": "At",
	"Def": "Def",
	"SpA": "At.E",
	"SpD": "Def.E",
	"Spe": "Vel",
	"Protip:": "Consejo:",
	"You can also set natures by typing + and - in the EV box.":
		"También puedes fijar naturalezas escribiendo + y - en el cuadro de EVs.",
	"Use a different nature to get higher stats": "Usa otra naturaleza para obtener mejores características",
	"Use a different nature to save": "Usa otra naturaleza para ahorrar",
	"Sample sets": "Sets de muestra",
	"Box sets": "Sets de caja",
	"Adamant": "Firme",
	"Bashful": "Tímida",
	"Bold": "Osada",
	"Brave": "Audaz",
	"Calm": "Serena",
	"Careful": "Cauta",
	"Docile": "Dócil",
	"Gentle": "Amable",
	"Hardy": "Fuerte",
	"Hasty": "Activa",
	"Impish": "Agitada",
	"Jolly": "Alegre",
	"Lax": "Floja",
	"Lonely": "Huraña",
	"Mild": "Mansa",
	"Modest": "Modesta",
	"Naive": "Ingenua",
	"Naughty": "Pícara",
	"Quiet": "Tranquila",
	"Quirky": "Rara",
	"Rash": "Alocada",
	"Relaxed": "Plácida",
	"Sassy": "Grosera",
	"Serious": "Seria",
	"Timid": "Miedosa",
	"Normal": "Normal",
	"Fighting": "Lucha",
	"Flying": "Volador",
	"Poison": "Veneno",
	"Ground": "Tierra",
	"Rock": "Roca",
	"Bug": "Bicho",
	"Ghost": "Fantasma",
	"Steel": "Acero",
	"Fire": "Fuego",
	"Water": "Agua",
	"Grass": "Planta",
	"Electric": "Eléctrico",
	"Psychic": "Psíquico",
	"Ice": "Hielo",
	"Dragon": "Dragón",
	"Dark": "Siniestro",
	"Fairy": "Hada",
	"Stellar": "Estelar",

	// ——— Weather / field UI ———
	"Sun": "Sol",
	"Intense Sun": "Sol abrasador",
	"Rain": "Lluvia",
	"Heavy Rain": "Diluvio",
	"Sandstorm": "Tormenta de arena",
	"Hail": "Granizo",
	"Snow": "Nieve",
	"Strong Winds": "Vientos fuertes",
	"or": "o",
	"turn": "turno",
	"turns": "turnos",
	"Foe's ": "Del rival ",

	// ——— Battle tooltips ———
	"Accuracy": "Precisión",
	"can't miss": "no falla",
	"Base power": "Potencia base",
	"Base power vs": "Potencia base vs",
	"Z-Effect": "Efecto Z",
	"Calls": "Invoca",
	"priority": "prioridad",
	"Nearly always moves first": "Casi siempre ataca primero",
	"Nearly always moves last": "Casi siempre ataca último",
	"Usually moves first": "Normalmente ataca primero",
	"Hits both foes and ally.": "Golpea a ambos rivales y al aliado.",
	"Hits both foes.": "Golpea a ambos rivales.",
	"Hits adjacent foes and allies.": "Golpea a rivales y aliados adyacentes.",
	"Hits adjacent foes.": "Golpea a rivales adyacentes.",
	"Can target distant Pok\u00e9mon in Triples.": "Puede apuntar a Pok\u00e9mon lejanos en Triples.",
	"Hits all foes.": "Golpea a todos los rivales.",
	"Can target any foe in Free-For-All.": "Puede apuntar a cualquier rival en Free-For-All.",
	"The user thaws out if it is frozen.": "El usuario se descongela si est\u00e1 congelado.",
	"Not blocked by Protect": "No lo bloquea Protecci\u00f3n",
	"(and Detect, King's Shield, Spiky Shield)": "(ni Detecci\u00f3n, Escudo Real, Barrera Espinosa)",
	"Bypasses Substitute": "Ignora Sustituto",
	"(but does not break it)": "(pero no lo rompe)",
	"Not bounceable": "No rebota",
	"(can't be bounced by Magic Coat/Bounce)": "(no lo rebota Capa M\u00e1gica/Bote)",
	"Contact": "Contacto",
	"(triggers Iron Barbs, Spiky Shield, etc)": "(activa Puntas Met\u00e1licas, Barrera Espinosa, etc.)",
	"Sound": "Sonido",
	"(doesn't affect Soundproof pokemon)": "(no afecta a Pok\u00e9mon con Insonorizar)",
	"Powder": "Polvo",
	"(doesn't affect Grass, Overcoat, Safety Goggles)": "(no afecta a Planta, Funda ni Gafas Protectora)",
	"Fist": "Pu\u00f1o",
	"(boosted by Iron Fist)": "(potenciado por Pu\u00f1o F\u00e9rreo)",
	"Pulse": "Pulso",
	"(boosted by Mega Launcher)": "(potenciado por Megadisparador)",
	"Bite": "Mordisco",
	"(boosted by Strong Jaw)": "(potenciado por Mand\u00edbula Fuerte)",
	"Recoil": "Retroceso",
	"(boosted by Reckless)": "(potenciado por Audaz)",
	"Bullet-like": "Bal\u00edstico",
	"(doesn't affect Bulletproof pokemon)": "(no afecta a Pok\u00e9mon con Antibalas)",
	"Slicing": "Cortante",
	"(boosted by Sharpness)": "(potenciado por Cortante)",
	"Wind": "Viento",
	"(activates Wind Power and Wind Rider)": "(activa Poder E\u00f3lico y Jinete del Viento)",
	"No effect": "Sin efecto",
	"Mostly ineffective": "Casi ineficaz",
	"Not very effective": "No muy eficaz",
	"Extremely effective": "Extremadamente eficaz",
	"Super effective": "Supereficaz",
	"vs.": "vs.",

	// ——— Battle log / rules ———
	"Turn": "Turno",
	"Format:": "Formato:",
	"Rated battle": "Batalla puntuada",
	"Species Clause": "Cl\u00e1usula de especies",
	"Limit one of each Pok\u00e9mon": "L\u00edmite de uno de cada Pok\u00e9mon",
	"HP Percentage Mod": "Mod. de PS en porcentaje",
	"HP is shown in percentages": "Los PS se muestran en porcentajes",
	"Sleep Clause Mod": "Mod. cl\u00e1usula de sue\u00f1o",
	"Limit one foe put to sleep": "L\u00edmite de un rival dormido",
	"Illusion Level Mod": "Mod. nivel de Ilusi\u00f3n",
	"Illusion disguises the Pok\u00e9mon's true level": "Ilusi\u00f3n oculta el nivel real del Pok\u00e9mon",
	"Nickname Clause": "Cl\u00e1usula de mote",
	"OHKO Clause": "Cl\u00e1usula OHKO",
	"Evasion Clause": "Cl\u00e1usula de evasi\u00f3n",
	"Evasion Items Clause": "Cl\u00e1usula de objetos de evasi\u00f3n",
	"Evasion Moves Clause": "Cl\u00e1usula de movimientos de evasi\u00f3n",
	"Endless Battle Clause": "Cl\u00e1usula de batalla infinita",
	"Item Clause": "Cl\u00e1usula de objeto",
	"Team Preview": "Vista previa del equipo",
	"Cancel Mod": "Mod. cancelar",
	"Obtainable": "Obtenible",
	"Battle timer is ON: inactive players will automatically lose when time's up.": "Temporizador de batalla ACTIVADO: los jugadores inactivos perder\u00e1n autom\u00e1ticamente al agotarse el tiempo.",
	"Battle timer is now OFF.": "El temporizador de batalla est\u00e1 ahora DESACTIVADO.",
	"Time left:": "Tiempo restante:",
	"sec this turn": "s este turno",
	"sec total": "s en total",
	"sec grace": "s de gracia",
	"(requested by": "(solicitado por",
	"has": "le quedan",
	"seconds left.": "segundos.",
	"seconds left this turn.": "segundos en este turno.",
	"You have": "Te quedan",
	"15 seconds left this turn.": "15 segundos en este turno.",

	// Battle tooltips
	"Turns asleep:": "Turnos dormido:",
	"Possible abilities:": "Habilidades posibles:",
	"Ability:": "Habilidad:",
	"Item:": "Objeto:",
	"Spe": "Vel",
	"(before external modifiers)": "(antes de modificadores externos)",
	"(before stat stage changes)": "(antes de cambios de características)",
	"base:": "base:",
	"Terastallized": "Teracristalizado",
	"Type changed": "Tipo cambiado",
	"Transformed into": "Transformado en",
	"Changed forme:": "Forma cambiada:",
	"Possible Illusion": "Posible Ilusión",
	"pixels": "píxeles",
	"Terastallized target": "Objetivo teracristalizado",
	"(fainted)": "(debilitado)",
	"Next damage:": "Próximo daño:",
	"Would take if ability removed:": "Daño si se quita la habilidad:",
	"HP:": "PS:",
	"Tera Type:": "Tipo Tera:",
	"was consumed": "fue consumido",
	"was eaten": "fue comido",
	"was popped": "estalló",
	"was stolen": "fue robado",
	"was knocked off": "fue derribado",
	"was flung": "fue lanzado",
	"was incinerated": "fue incinerado",
	"was held up": "fue retenido",
	"was given away": "fue regalado",
	"was frisked": "fue fisgado",
	"consumed": "consumido",
	"eaten": "comido",
	"popped": "estallado",
	"stolen": "robado",
	"knocked off": "derribado",
	"flung": "lanzado",
	"incinerated": "incinerado",
	"held up": "retenido",
	"given away": "regalado",
	"frisked": "fisgado",
	"was": "fue",

	// Formats / UI
	"Random Battle": "Combate Aleatorio",
	"Unrated Random Battle": "Combate Aleatorio sin rango",
	"Free-For-All Random Battle": "Combate Aleatorio todos contra todos",
	"Random Battle (Blitz)": "Combate Aleatorio (Blitz)",
	"Multi Random Battle": "Combate Aleatorio Multi",
	"Random Doubles Battle": "Combate Aleatorio Dobles",
	"Random Triples Battle": "Combate Aleatorio Triples",
	"Monotype Random Battle": "Combate Aleatorio Monotype",
	"Random Battle Mayhem": "Combate Aleatorio Caos",
	"Random Battle (Shared Power, B12P6)": "Combate Aleatorio (Poder compartido, B12P6)",
	"Random Battle (B12P6, No Team Preview)": "Combate Aleatorio (B12P6, sin vista previa)",
	"Battle Factory": "Fábrica de Combates",
	"1v1 Factory": "Fábrica 1v1",
	"BSS Factory": "Fábrica BSS",
	"Draft Factory": "Fábrica Draft",
	"Baby Random Battle": "Combate Aleatorio Bebé",
	"Hackmons Cup": "Copa Hackmons",
	"Doubles Hackmons Cup": "Copa Hackmons Dobles",
	"Broken Cup": "Copa Rota",
	"Challenge Cup": "Copa Desafío",
	"Challenge Cup 1v1": "Copa Desafío 1v1",
	"Challenge Cup 2v2": "Copa Desafío 2v2",
	"Challenge Cup 6v6": "Copa Desafío 6v6",
	"Metronome Battle": "Combate Metrónomo",
	"Random Roulette": "Ruleta aleatoria",
	"Custom Game": "Partida personalizada",
	"Doubles Custom Game": "Partida personalizada dobles",
	"Triples Custom Game": "Partida personalizada triples",
	"Doubles OU": "Dobles OU",
	"Doubles Ubers": "Dobles Ubers",
	"Doubles UU": "Dobles UU",
	"Doubles LC": "Dobles LC",
	"2v2 Doubles": "Dobles 2v2",
	"4v4 Doubles UU": "Dobles 4v4 UU",
	"4v4 Doubles Draft": "Draft Dobles 4v4",
	"6v6 Doubles Draft": "Draft Dobles 6v6",
	"Anything Goes": "Todo vale",
	"Free-For-All": "Todos contra todos",
	"Almost Any Ability": "Casi cualquier habilidad",
	"Balanced Hackmons": "Hackmons equilibrados",
	"Godly Gift": "Regalo divino",
	"Mix and Mega": "Mix and Mega",
	"Mix and Mega Doubles": "Mix and Mega Dobles",
	"Mix and Mega LC": "Mix and Mega LC",
	"Shared Power": "Poder compartido",
	"Pure Hackmons": "Hackmons puros",
	"Trademarked": "Trademarked",
	"Monocolor": "Monocolor",
	"Partners in Crime": "Socios del crimen",
	"Inheritance": "Herencia",
	"Inverse": "Inverso",
	"Nature Swap": "Cambio de naturaleza",
	"Tier Shift": "Cambio de tier",
	"National Dex Monoletter": "Dex Nacional Monoletra",
	"Mix and Mega: Limited Supply Random Battle": "Mix and Mega: Combate Aleatorio de suministro limitado",
	"National Dex": "Dex Nacional",
	"National Dex AG": "Dex Nacional AG",
	"National Dex Monotype": "Dex Nacional Monotype",
	"National Dex UU": "Dex Nacional UU",
	"National Dex RU": "Dex Nacional RU",
	"National Dex LC": "Dex Nacional LC",
	"National Dex Ubers": "Dex Nacional Ubers",
	"National Dex Ubers UU": "Dex Nacional Ubers UU",
	"National Dex Doubles": "Dobles Dex Nacional",
	"National Dex Doubles Ubers": "Dobles Ubers Dex Nacional",
	"National Dex 1v1": "Dex Nacional 1v1",
	"National Dex AAA": "Dex Nacional AAA",
	"National Dex BH": "Dex Nacional BH",
	"National Dex Godly Gift": "Dex Nacional Regalo divino",
	"National Dex STABmons": "Dex Nacional STABmons",
	"National Dex Draft": "Draft Dex Nacional",
	"NatDex Draft": "Draft Dex Nacional",
	"NatDex 6v6 Doubles Draft": "Draft Dobles 6v6 Dex Nacional",
	"NatDex LC Draft": "Draft LC Dex Nacional",
	"NatDex 4v4 Doubles Draft": "Draft Dobles 4v4 Dex Nacional",
	"Super Staff Bros Ultimate": "Super Staff Bros Ultimate",
	"Eternity Super Staff Bros Ultimate": "Eternity Super Staff Bros Ultimate",
	"Battle Stadium Singles": "Battle Stadium Individuales",
	"Battle Stadium Doubles": "Battle Stadium Dobles",
	"Battle Spot Singles": "Battle Spot Individuales",
	"Battle Spot Doubles": "Battle Spot Dobles",
	"Battle Spot Triples": "Battle Spot Triples",
	"Battle Festival Doubles": "Festival de Combates Dobles",
	"Modo entrenamiento": "Modo entrenamiento",
	"NU Doubles": "Dobles NU",

	// Format list sections
	"S/V Singles": "Individuales E/P",
	"S/V Doubles": "Dobles E/P",
	"Champions": "Champions",
	"Unofficial Metagames": "Metajuegos no oficiales",
	"Draft": "Draft",
	"Ladder Spotlight": "Destacados de la ladder",
	"Other Metagames": "Otros metajuegos",
	"National Dex Other Tiers": "Otras tiers Dex Nacional",
	"Randomized Metas": "Metas aleatorias",
	"Past Gens OU": "OU de gens pasadas",
	"Past Gens Doubles OU": "Dobles OU de gens pasadas",
	"Past Generations": "Generaciones pasadas",
	"Challengeable OMs": "OMs retables",
	"Temporary Tour Metas": "Metas temporales de torneo",
	"Pet Mods": "Pet Mods",
	"Sw/Sh Singles": "Individuales Espada/Escudo",
	"Sw/Sh Doubles": "Dobles Espada/Escudo",
	"US/UM Singles": "Individuales US/UM",
	"US/UM Doubles": "Dobles US/UM",
	"OR/AS Singles": "Individuales OR/AS",
	"OR/AS Doubles/Triples": "Dobles/Triples OR/AS",
	"B2/W2 Singles": "Individuales B2/N2",
	"B2/W2 Doubles": "Dobles B2/N2",
	"DPP Singles": "Individuales DPP",
	"DPP Doubles": "Dobles DPP",
	"Liga Market": "Liga Market",
	"Eternity — Random": "Eternity — Aleatorio",
	"Copa del Rey": "Copa del Rey",
	"PokéMMO": "PokéMMO",
	"No Format": "Sin formato",

	"Home": "Inicio",
	"Lobby": "Lobby",
	"Staff": "Staff",
	"Rooms": "Salas",
	" users": " usuarios",
	" user": " usuario",
	"(empty room)": "(sala vacía)",
	"Uploaded Replay": "Replay subido",
	"Console": "Consola",
	"1 line from USER hidden": "1 línea de USER oculta",
	"N lines from USER hidden": "N líneas de USER ocultas",
	"Hide 1 line from USER": "Ocultar 1 línea de USER",
	"Hide N lines from USER": "Ocultar N líneas de USER",
	"N of NAME's messages were cleared from ROOM by BY.": "N mensajes de NAME fueron borrados de ROOM por BY.",
	"NAME's messages were cleared from ROOM by BY.": "Los mensajes de NAME fueron borrados de ROOM por BY.",
	"NAME's alts messages were cleared from ROOM by BY.": "Los mensajes de los alts de NAME fueron borrados de ROOM por BY.",
	// ——— Battle overlays / teambuilder gaps ———
	"Paralyzed": "Paralizado",
	"Frozen": "Congelado",
	"Asleep": "Dormido",
	"Loafing around": "Holgazaneando",
	"Must recharge": "Debe recargar",
	"Lost focus": "Perdió la concentración",
	"Trap failed": "Trampa fallida",
	"Flinched": "Retrocedió",
	"Immobilized": "Inmovilizado",
	"already ": "ya ",
	"Stats swapped": "Stats intercambiados",
	"Boosts lost": "Mejoras perdidas",
	"Restored": "Restaurado",
	"Stats copied": "Stats copiados",
	"Stats reset": "Stats reiniciados",
	"Stats inverted": "Stats invertidos",
	"Critical hit": "Golpe crítico",
	"Super-effective": "Muy eficaz",
	"Resisted": "Resistido",
	"Immune": "Inmune",
	"Missed": "Falló",
	"Already burned": "Ya quemado",
	"Already poisoned": "Ya envenenado",
	"Failed": "Falló",
	"Already asleep": "Ya dormido",
	"Already paralyzed": "Ya paralizado",
	"Already frozen": "Ya congelado",
	"Stat drop blocked": "Bajada bloqueada",
	"Quick Guard": "Anticipo",
	"Wide Guard": "Vasta Guardia",
	"Crafty Shield": "Truco Defensa",
	"Protected": "Protegido",
	"Burned": "Quemado",
	"Toxic poison": "Envenen. grave",
	"Poisoned": "Envenenado",
	"Burn cured": "Quemadura curada",
	"Poison cured": "Veneno curado",
	"Woke up": "Despertó",
	"Paralysis cured": "Parálisis curada",
	"Thawed": "Descongelado",
	"Cured": "Curado",
	"Team Cured": "Equipo curado",
	"Balloon": "Globo",
	"Item knocked off": "Objeto derribado",
	"Balloon popped": "Globo estallado",
	"Sash": "Banda Focus",
	"Focus Band": "Cinta Focus",
	"Transformed": "Transformado",
	"Power Trick": "Truco Fuerza",
	"Identified": "Identificado",
	"Telekinesis": "Telequinesis",
	"Confused": "Confuso",
	"Heal Block": "Anticura",
	"Drowsy": "Somnoliento",
	"Taunted": "Mofado",
	"Imprisoning": "Sellar",
	"Disabled": "Anulado",
	"Embargo": "Embargo",
	"Tormented": "Tormento",
	"Ingrained": "Arraigo",
	"Aqua Ring": "Acua Aro",
	"Stockpile": "Reserva",
	"Stockpile&times;2": "Reserva&times;2",
	"Stockpile&times;3": "Reserva&times;3",
	"Perish next turn": "Debilitado próximo turno",
	"Perish in 2": "Debilitado en 2",
	"Perish in 3": "Debilitado en 3",
	"Encored": "Otra vez",
	"Bide": "Venganza",
	"Attracted": "Enamorado",
	"Lightened": "Aligerado",
	"+Crit rate": "+Índice crítico",
	"Cursed": "Maldito",
	"Nightmare": "Pesadilla",
	"Magnet Rise": "Levitón",
	"Smacked Down": "Antiaéreo",
	"Damage": "Daño",
	"Blocked": "Bloqueado",
	"Mist": "Neblina",
	"Light Screen": "Pantalla Luz",
	"Reflect": "Reflejo",
	"Telekinesis&nbsp;ended": "Fin telequinesis",
	"Confusion&nbsp;ended": "Fin confusión",
	"De-seeded": "Sin drenadoras",
	"Heal Block ended": "Fin anticura",
	"Attract&nbsp;ended": "Fin enamoramiento",
	"Taunt&nbsp;ended": "Fin mofa",
	"Disable&nbsp;ended": "Fin anulación",
	"Embargo ended": "Fin embargo",
	"Torment&nbsp;ended": "Fin tormento",
	"Encore&nbsp;ended": "Fin bis",
	"Illusion ended": "Fin ilusión",
	"Slow Start ended": "Fin inicio lento",
	"Faded": "Desvanecido",
	"Landed": "Aterrizó",
	"Mat Block": "Escudo Tatami",
	"Enduring": "Aguante",
	"Helping Hand": "Refuerzo",
	"Focusing": "Concentrado",
	"Trap set": "Trampa lista",
	"Beak Blast": "Pico Cañón",
	"Grudge": "Rabia",
	"Destiny Bond": "Destino Ligado",
	"Protection broken": "Protección rota",
	"Heal": "Cura",
	"Item Stolen": "Objeto robado",
	"Dynamaxed": "Dynamax",
	"Throat Chop": "Golpe Garganta",
	"Flash Fire": "Absorbe Fuego",
	"Imprisoning foe": "Sellando al rival",
	"Miracle Eye": "Gran Ojo",
	"Foresight": "Profecía",
	"Infatuation": "Enamoramiento",
	"Torment": "Tormento",
	"Taunt": "Mofa",
	"Disable": "Anulación",
	"Ingrain": "Arraigo",
	"Perish now": "Debilitado ahora",
	"Leech Seed": "Drenadoras",
	"Encore": "Otra vez",
	"Critical Hit Boost": "Más críticos",
	"Slow Start": "Inicio lento",
	"Protosynthesis: Atk": "Protosíntesis: Atq",
	"Protosynthesis: Def": "Protosíntesis: Def",
	"Protosynthesis: SpA": "Protosíntesis: AtEsp",
	"Protosynthesis: SpD": "Protosíntesis: DefEsp",
	"Protosynthesis: Spe": "Protosíntesis: Vel",
	"Quark Drive: Atk": "Motor Cuántico: Atq",
	"Quark Drive: Def": "Motor Cuántico: Def",
	"Quark Drive: SpA": "Motor Cuántico: AtEsp",
	"Quark Drive: SpD": "Motor Cuántico: DefEsp",
	"Quark Drive: Spe": "Motor Cuántico: Vel",
	"Fallen: 1": "Caídos: 1",
	"Fallen: 2": "Caídos: 2",
	"Fallen: 3": "Caídos: 3",
	"Fallen: 4": "Caídos: 4",
	"Fallen: 5": "Caídos: 5",
	"No Retreat": "Bastión Final",
	"Octolock": "Octopresa",
	"Tar Shot": "Alquitranazo",
	"Salt Cure": "Salazón",
	"Syrupy": "Siroposo",
	"Mimic": "Mimetismo",
	"Water Sport": "Hidrochorro",
	"Mud Sport": "Chapoteo Lodo",
	"Uproar": "Alboroto",
	"Rage": "Furia",
	"Protect": "Protección",
	"Max Guard": "Maxibarrera",
	"Magic Coat": "Capa Mágica",
	"Snatch": "Robo",
	"Charge": "Carga",
	"Endure": "Aguante",
	"Electrify": "Electrificación",
	"Glaive Rush": "Asalto Espadón",
	"Rage Powder": "Polvo Ira",
	"Follow Me": "Señuelo",
	"Instruct": "Mandato",
	"Laser Focus": "Foco Láser",
	"Spotlight": "Foco",
	"Bind": "Atadura",
	"Clamp": "Tenaza",
	"Fire Spin": "Giro Fuego",
	"Infestation": "Acoso",
	"Magma Storm": "Lluvia Ígnea",
	"Sand Tomb": "Bucle Arena",
	"Snap Trap": "Cepo",
	"Thunder Cage": "Electrojaula",
	"Whirlpool": "Torbellino",
	"Wrap": "Constricción",
	"Evasion": "Evasión",
	"You might have some moves disabled, so you won't be able to cancel an attack!": "¡Puede que tengas movimientos anulados, así que no podrás cancelar un ataque!",
	"You might be locked into a move.": "Puede que estés obligado a un movimiento.",
	"(prevents switching if you're locked)": "(impide cambiar si estás obligado)",
	"You might be trapped, so you won't be able to cancel a switch!": "¡Puede que estés atrapado, así que no podrás cancelar un cambio!",
	"You're trapped and cannot switch!": "¡Estás atrapado y no puedes cambiar!",
	"Choose a Pokémon to revive!": "¡Elige un Pokémon para revivir!",
	"Automatic choice": "Elección automática",
	"Unrecognized choice from server: ": "Elección no reconocida del servidor: ",
	" is locked into a move.": " está obligado a un movimiento.",
	" will ": " ",
	"Terastallize (": "Teracristalizar (",
	") and ": ") y ",
	"Gigantamax and ": "Gigamax y ",
	"Dynamax and ": "Dynamax y ",
	"use ": "usará ",
	" at slot ": " en el puesto ",
	" at ": " contra ",
	" at ally slot ": " en el puesto aliado ",
	" at ally ": " contra el aliado ",
	" will revive ": " revivirá a ",
	" will switch to ": " cambiará a ",
	"shift": "se desplazará",
	" to the center.": " al centro.",
	"You picked ": "Elegiste ",
	"lead": "líder",
	"slot ": "puesto ",
	"Replay link:\n": "Enlace del replay:\n",
	"(After stat modifiers:)": "(Tras modificadores de características:)",
	"(revealed)": "(revelado)",
	"(no conditions)": "(sin condiciones)",
	"(no weather)": "(sin clima)",
	"Add Pokémon": "Añadir Pokémon",
	"Add Pokemon": "Añadir Pokémon",
	"Clipboard": "Portapapeles",
	"Fetching Paste...": "Obteniendo paste...",
	"IV spreads": "Distribuciones de IVs",
	"Auto (...)": "Auto (...)",
	"min Atk": "mín Atq",
	"min Atk, min Spe": "mín Atq, mín Vel",
	"max all": "máx todo",
	"min Spe": "mín Vel",
	"Hidden Power {type} IVs": "IVs de Poder Oculto {type}",
	"Guessed spread:": "Distribución estimada:",
	"(Please choose 4 moves to get a guessed spread)": "(Elige 4 movimientos para estimar la distribución)",
	"Smogon analysis": "Análisis de Smogon",
	"Natures cannot raise or lower HP.": "Las naturalezas no pueden subir o bajar los PS.",
	"Minus {stat} Nature": "Naturaleza −{stat}",
	"Plus {stat} Nature": "Naturaleza +{stat}",
	"resist": "resiste",
	"weak": "débil",
	"See all": "Ver todo",
	"Search species or filter by type, learnable moves, ability, tier, or egg group": "Buscar especies o filtrar por tipo, movimientos, habilidad, tier o grupo huevo",
	"Search abilities": "Buscar habilidades",
	"Search items": "Buscar objetos",
	"Search moves or filter by type or category": "Buscar movimientos o filtrar por tipo o categoría",
	"Box": "Caja",
	"Untitled": "Sin título",
	"Sort: ": "Orden: ",
	"Filters: ": "Filtros: ",
	"(backspace = delete filter)": "(retroceso = quitar filtro)",
	"Show previous search results": "Mostrar resultados anteriores",
	"Show next search results": "Mostrar siguientes resultados",
	"Error: not found": "Error: no encontrado",
	"Unrecognized pokemon": "Pokémon no reconocido",
	"Unrecognized item": "Objeto no reconocido",
	"Unrecognized ability": "Habilidad no reconocida",
	"Unrecognized move": "Movimiento no reconocido",
	"(slot N empty)": "(puesto N vacío)",
	"(egg group)": "(grupo huevo)",
	"(tier)": "(tier)",
	"(article)": "(artículo)",
	"(search type)": "(tipo de búsqueda)",
	"Types": "Tipos",
	"Abilities": "Habilidades",
	"Cat": "Cat",
	"Pow": "Pot",
	"Acc": "Prec",
	"Power": "Potencia",
	"Filter": "Filtro",
	"Illegal": "Ilegal",
	"Illegal Pokémon": "Pokémon ilegal",
	"Illegal results": "Resultados ilegales",
	"Hidden Ability": "Habilidad oculta",
	"Special Event Ability": "Habilidad de evento",
	"Situational Abilities": "Habilidades situacionales",
	"Unviable Abilities": "Habilidades inviables",
	"CAP moves": "Movimientos CAP",
	"Usually useless moves": "Movimientos poco útiles",
	"Sketched moves": "Movimientos bosquejados",
	"Useless sketched moves": "Bosquejos inútiles",
	"Egg group": "Grupo huevo",
	"Category": "Categoría",
	"Article": "Artículo",
	"Tiers": "Tiers",
	"Items": "Objetos",
	"Popular items": "Objetos populares",
	"Usually useless items": "Objetos poco útiles",
	"Popular abilities": "Habilidades populares",
	"Abilities": "Habilidades",
	"Popular moves": "Movimientos populares",
	"NFEs not in a higher tier": "NFE fuera de un tier superior",
	"Specific to": "Específico de",
	"Pokémon": "Pokémon",
	"Restores negative stat stages to 0": "Restaura las características negativas a 0",
	"Crit ratio +2": "Índice de crítico +2",
	"Restores HP 100%": "Restaura el 100% de los PS",
	"Centers attention on the user": "Centra la atención en el usuario",

	"Restores HP 100% if user is Ghost type, otherwise Attack +1": "Restaura el 100% de los PS si es de tipo Fantasma; si no, Ataque +1",
	"Redirects opposing attacks to user": "Redirige los ataques rivales al usuario",
	"Restores replacement's HP 100%": "Restaura el 100% de los PS del relevo",
	"(More than 4 moves is usually a sign of Illusion Zoroark/Zorua.) ": "(Más de 4 movimientos suele indicar Ilusión de Zoroark/Zorua.) ",
	"(Pressure is not visible in Gen 3, so in certain situations, the exact amount of PP used may be unknown.) ": "(Presión no es visible en 3ª gen, así que a veces no se sabe el PP exacto gastado.) ",
	"(Your opponent has two indistinguishable Pokémon, making it impossible for you to tell which one has which moves/ability/item.) ": "(El rival tiene dos Pokémon indistinguibles: no se puede saber cuál tiene qué movimientos/habilidad/objeto.) ",

	"Type": "Tipo",
	"BST": "BST",
	"PP": "PP",

	"Hidden Power": "Poder Oculto",

};

export function getClientLanguage(): ClientLanguageID {
	const raw = PS.prefs.serversettings?.language;
	const id = toID(raw || '') as ClientLanguageID | '';
	return id || DEFAULT_CLIENT_LANGUAGE;
}

export function trUI(english: string): string {
	const lang = getClientLanguage();
	if (lang === 'spanish') return SPANISH[english] || english;
	return english;
}

/** «N usuario(s)» / «N user(s)» según idioma y cantidad. */
export function trUserCountLabel(count: number): string {
	const n = Math.max(0, Number(count) || 0);
	if (n === 1) return `1${trUI(' user')}`;
	return `${n}${trUI(' users')}`;
}

/** «N usuario(s) conectado(s)» para el contador del panel de salas. */
export function trUsersOnlineLabel(count: number): string {
	const n = Math.max(0, Number(count) || 0);
	if (n === 1) return `1 ${trUI('user online')}`;
	return `${n} ${trUI('users online')}`;
}

/** «N batalla(s) activa(s)» para el contador del panel de salas. */
export function trActiveBattlesLabel(count: number): string {
	const n = Math.max(0, Number(count) || 0);
	if (n === 1) return `1 ${trUI('active battle')}`;
	return `${n} ${trUI('active battles')}`;
}

/** Rangos oficiales de Showdown (no toca rangos custom: Áureo, Destacado, etc.). */
const OFFICIAL_RANK_ES: { [english: string]: string } = {
	Administrator: 'Administrador',
	Moderator: 'Moderador',
	Driver: 'Operador',
	Voice: 'Voz',
	'Room Owner': 'Dueño de sala',
	Host: 'Anfitrión',
	Bot: 'Bot',
	Player: 'Jugador',
	'Prize Winner': 'Ganador de premio',
	Locked: 'Bloqueado',
	Muted: 'Silenciado',
	Namelocked: 'Nombre bloqueado',
	Whitelist: 'Lista blanca',
};

function splitRankSymbol(name: string): { base: string, suffix: string } {
	const m = String(name || '').match(/^(.*?)(\s*\([^)]*\))\s*$/);
	if (!m) return { base: String(name || '').trim(), suffix: '' };
	return { base: m[1].trim(), suffix: m[2] };
}

/** "Driver (%)" → "Operador (%)" si el cliente está en castellano. */
export function trOfficialRankLabel(name: string): string {
	if (!name) return name;
	if (getClientLanguage() !== 'spanish') return name;
	const { base, suffix } = splitRankSymbol(name);
	const translated = OFFICIAL_RANK_ES[base];
	if (!translated) return name;
	return translated + suffix;
}

/** "Driver (%)" → "Operador global (%)" (o "Global Driver (%)" en inglés). */
export function trGlobalRankLabel(name: string): string {
	if (!name) return name;
	const localized = trOfficialRankLabel(name);
	if (getClientLanguage() !== 'spanish') return `${trUI("Global")} ${localized}`;
	const { base, suffix } = splitRankSymbol(localized);
	return `${base} global${suffix}`;
}

/**
 * Translate a format/section label for the format picker (and similar lists).
 * Tries exact string, dropdown-stripped form, then core name after [Gen N].
 */
export function trFormatDisplay(name: string): string {
	if (!name) return name;
	if (getClientLanguage() !== 'spanish') {
		return name.replace('[Gen 8 ', '[').replace('[Gen 9] ', '').replace('[Gen 7 ', '[');
	}
	const display = name.replace('[Gen 8 ', '[').replace('[Gen 9] ', '').replace('[Gen 7 ', '[');
	if (SPANISH[display]) return SPANISH[display];
	if (SPANISH[name]) return SPANISH[name];

	const coreMatch = display.match(/^(?:\[[^\]]*\]\s*)?(.+)$/);
	const core = (coreMatch?.[1] || display).trim();
	if (SPANISH[core]) {
		const prefixLen = display.length - core.length;
		return (prefixLen > 0 ? display.slice(0, prefixLen) : '') + SPANISH[core];
	}
	return display;
}

/** Template helper: trUI`Hello` or interpolate later if needed. */
export function trUIf(strings: TemplateStringsArray, ...values: unknown[]): string {
	const english = strings.reduce((out, s, i) => out + s + (i < values.length ? String(values[i]) : ''), '');
	return trUI(english);
}

export function ensureClientLanguageSynced(send = true) {
	const current = PS.prefs.serversettings || {};
	const language = (toID(current.language || '') as ClientLanguageID | '') || DEFAULT_CLIENT_LANGUAGE;
	if (current.language !== language) {
		PS.prefs.set('serversettings', { ...current, language });
	}
	if (send) {
		PS.send(`/updatesettings ${JSON.stringify(PS.prefs.serversettings)}`);
	}
	return language;
}
